import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { Tag, RefreshCw, Edit3, Trash2, Ban, CheckCircle2, X, Eye, MousePointerClick, SlidersHorizontal, StopCircle, PlusCircle, RotateCcw, Settings2 } from 'lucide-react';

/**
 * Admin — Price-2-Day (Promotions + Settings).
 * Promotions: full control (view all, edit, end early, extend, suspend/reinstate,
 * archive, set allowance, reset free-intro). Settings: admin-editable durations,
 * prices, currency, intro, limits and enable/disable toggles (platform_config).
 * Per-promotion approval is NOT required. Reads/writes only promotion collections.
 */
const money = (v, c) => (v == null ? '—' : `${c || 'EUR'} ${Number(v).toFixed(2)}`);
const centsStr = (c, ccy) => `${ccy || 'EUR'} ${(Number(c || 0) / 100).toFixed(2)}`;
const STATUS_STYLE = {
  live: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-sky-100 text-sky-700',
  pending: 'bg-amber-100 text-amber-700',
  suspended: 'bg-rose-100 text-rose-700',
  expired: 'bg-gray-200 text-gray-600',
  archived: 'bg-gray-200 text-gray-500',
};
const PAY_STYLE = {
  free_intro: 'bg-emerald-50 text-emerald-700',
  free_allowance: 'bg-emerald-50 text-emerald-700',
  test_mode: 'bg-amber-50 text-amber-700',
  pending_payment: 'bg-rose-50 text-rose-700',
};

const AdminPricePromotions = () => {
  const [section, setSection] = useState('promotions');
  const [promos, setPromos] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('all');
  const [q, setQ] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage({ type: '', text: '' }), 4000); };

  const fetchPromos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/price/promotions', { params: { view, q } });
      setPromos(res.data?.promotions || []);
    } catch (err) {
      flash('error', `Error loading promotions: ${err.response?.data?.detail || err.message}`);
    } finally { setLoading(false); }
  }, [view, q]);

  const fetchSettings = useCallback(async () => {
    try { const res = await axiosInstance.get('/admin/price/settings'); setSettings(res.data); }
    catch (err) { flash('error', err.response?.data?.detail || err.message); }
  }, []);

  useEffect(() => { fetchPromos(); fetchSettings(); }, [fetchPromos, fetchSettings]);

  const act = async (fn, id, okMsg) => {
    setBusy(id);
    try { await fn(); flash('success', okMsg); fetchPromos(); }
    catch (err) { flash('error', err.response?.data?.detail || err.message); }
    finally { setBusy(null); }
  };

  const saveEdit = () => act(async () => {
    await axiosInstance.put(`/admin/price/promotions/${editModal.id}`, {
      productName: editModal.productName,
      description: editModal.description,
      promoPrice: editModal.promoPrice != null && editModal.promoPrice !== '' ? Number(editModal.promoPrice) : undefined,
      normalPrice: editModal.normalPrice != null && editModal.normalPrice !== '' ? Number(editModal.normalPrice) : undefined,
      endDate: editModal.endDate,
    });
    setEditModal(null);
  }, editModal?.id, 'Promotion updated');

  const extend = (p) => {
    const opts = (settings?.durations || []).map((d) => d.days).join(' / ') || '7 / 14 / 30';
    const val = window.prompt(`Extend "${p.productName}" by how many days? (${opts})`, String(settings?.durations?.[0]?.days || 7));
    if (val == null) return;
    act(async () => { await axiosInstance.post(`/admin/price/promotions/${p.id}/extend`, { days: Number(val) }); }, p.id, 'Promotion extended');
  };

  const setAllowance = (p) => {
    const val = window.prompt(`Set active-promotion allowance for "${p.businessName}" (blank uses the global max)`, '5');
    if (val == null) return;
    act(async () => { await axiosInstance.put(`/admin/price/business/${p.businessId}/allowance`, { allowance: Number(val) }); }, p.id, 'Allowance updated');
  };

  const resetIntro = (p) => {
    if (!window.confirm(`Reset the FREE introductory promotion for "${p.businessName}"? They will be able to claim one free 7-day promotion again.`)) return;
    act(async () => { await axiosInstance.post(`/admin/price/business/${p.businessId}/reset-intro`); }, p.id, 'Free intro reset');
  };

  return (
    <div data-testid="admin-price-promotions">
      {message.text && (
        <div className={`mb-3 p-3 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`} data-testid="admin-promo-message">{message.text}</div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <Tag className="h-5 w-5 text-indigo-600" />
        <span className="font-semibold text-gray-800">Price-2-Day</span>
        <div className="ml-3 flex gap-1 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setSection('promotions')} className={`px-3 py-1 rounded-md text-sm ${section === 'promotions' ? 'bg-white shadow text-indigo-700 font-medium' : 'text-gray-600'}`} data-testid="admin-subtab-promotions">Promotions</button>
          <button onClick={() => setSection('settings')} className={`px-3 py-1 rounded-md text-sm ${section === 'settings' ? 'bg-white shadow text-indigo-700 font-medium' : 'text-gray-600'}`} data-testid="admin-subtab-settings">Settings</button>
        </div>
      </div>

      {section === 'promotions' && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product / shop" className="h-9 px-3 rounded-lg border text-sm" data-testid="admin-promo-search" />
            <select value={view} onChange={(e) => setView(e.target.value)} className="h-9 px-2 rounded-lg border text-sm" data-testid="admin-promo-filter">
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="scheduled">Scheduled</option>
              <option value="live">Live</option>
              <option value="expired">Expired</option>
              <option value="suspended">Suspended</option>
              <option value="archived">Archived</option>
            </select>
            <button onClick={fetchPromos} className="h-9 px-3 rounded-lg border text-sm inline-flex items-center gap-1" data-testid="admin-promo-refresh"><RefreshCw className="h-4 w-4" />Refresh</button>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
          ) : promos.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center" data-testid="admin-promo-empty">No promotions found.</p>
          ) : (
            <div className="space-y-2" data-testid="admin-promo-list">
              {promos.map((p) => (
                <div key={p.id} className="rounded-xl border p-3 flex flex-wrap items-start gap-3" data-testid={`admin-promo-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.productName}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[p.statusLabel] || 'bg-gray-100'}`} data-testid={`admin-promo-status-${p.id}`}>{p.statusLabel}</span>
                      {p.paymentStatus && <span className={`text-[10px] px-2 py-0.5 rounded-full ${PAY_STYLE[p.paymentStatus] || 'bg-gray-100 text-gray-600'}`} data-testid={`admin-promo-pay-${p.id}`}>{p.paymentStatus}</span>}
                      {p.isPreviewTest && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">PREVIEW TEST</span>}
                    </div>
                    <p className="text-[12px] text-gray-500">{p.businessName} · {p.town || '—'} · intro: {p.businessIntroUsed ? 'USED' : 'AVAILABLE'}</p>
                    <p className="text-[12px]"><span className="font-bold text-indigo-700">{money(p.promoPrice, p.currency)}</span>{p.normalPrice != null && <span className="line-through ml-2 text-gray-400">{money(p.normalPrice, p.currency)}</span>}{p.packSize ? ` · ${p.packSize}` : ''}</p>
                    <p className="text-[11px] text-gray-400">{p.durationDays ? `${p.durationDays} days · ` : ''}{(p.startDate || '').slice(0, 10)} → {(p.endDate || '').slice(0, 10)}</p>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                      <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{p.views ?? 0}</span>
                      <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{p.clicks ?? 0}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 shrink-0">
                    <button onClick={() => setEditModal({ ...p })} disabled={busy === p.id} className="p-1.5 rounded-lg bg-gray-100 text-gray-700" title="Edit" data-testid={`admin-promo-edit-${p.id}`}><Edit3 className="h-4 w-4" /></button>
                    {(p.statusLabel === 'live' || p.statusLabel === 'scheduled') && (
                      <button onClick={() => act(async () => { await axiosInstance.post(`/admin/price/promotions/${p.id}/end-early`); }, p.id, 'Ended early')} disabled={busy === p.id} className="p-1.5 rounded-lg bg-amber-50 text-amber-700" title="End early" data-testid={`admin-promo-endearly-${p.id}`}><StopCircle className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => extend(p)} disabled={busy === p.id} className="p-1.5 rounded-lg bg-sky-50 text-sky-700" title="Extend" data-testid={`admin-promo-extend-${p.id}`}><PlusCircle className="h-4 w-4" /></button>
                    {p.statusLabel === 'suspended'
                      ? <button onClick={() => act(async () => { await axiosInstance.post(`/admin/price/promotions/${p.id}/unsuspend`); }, p.id, 'Reinstated')} disabled={busy === p.id} className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700" title="Reinstate" data-testid={`admin-promo-unsuspend-${p.id}`}><CheckCircle2 className="h-4 w-4" /></button>
                      : <button onClick={() => act(async () => { await axiosInstance.post(`/admin/price/promotions/${p.id}/suspend`); }, p.id, 'Suspended')} disabled={busy === p.id} className="p-1.5 rounded-lg bg-rose-50 text-rose-700" title="Suspend" data-testid={`admin-promo-suspend-${p.id}`}><Ban className="h-4 w-4" /></button>}
                    <button onClick={() => act(async () => { await axiosInstance.post(`/admin/price/promotions/${p.id}/archive`); }, p.id, 'Archived')} disabled={busy === p.id} className="p-1.5 rounded-lg bg-gray-100 text-gray-600" title="Archive / remove" data-testid={`admin-promo-archive-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                    <button onClick={() => setAllowance(p)} disabled={busy === p.id} className="p-1.5 rounded-lg bg-indigo-50 text-indigo-700" title="Set business allowance" data-testid={`admin-promo-allowance-${p.id}`}><SlidersHorizontal className="h-4 w-4" /></button>
                    <button onClick={() => resetIntro(p)} disabled={busy === p.id} className="p-1.5 rounded-lg bg-purple-50 text-purple-700" title="Reset free intro" data-testid={`admin-promo-resetintro-${p.id}`}><RotateCcw className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {section === 'settings' && (
        <AdminPromoSettings settings={settings} onSaved={(s) => { setSettings(s); flash('success', 'Settings saved'); }} onError={(m) => flash('error', m)} />
      )}

      {editModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-promo-edit-modal">
          <div className="bg-white rounded-2xl p-4 w-full max-w-md space-y-3">
            <div className="flex items-center justify-between"><p className="font-semibold">Edit promotion</p><button onClick={() => setEditModal(null)}><X className="h-5 w-5" /></button></div>
            <label className="text-xs text-gray-500">Product</label>
            <input value={editModal.productName || ''} onChange={(e) => setEditModal({ ...editModal, productName: e.target.value })} className="w-full h-9 px-3 rounded-lg border text-sm" data-testid="admin-promo-edit-product" />
            <label className="text-xs text-gray-500">Description</label>
            <input value={editModal.description || ''} onChange={(e) => setEditModal({ ...editModal, description: e.target.value })} className="w-full h-9 px-3 rounded-lg border text-sm" data-testid="admin-promo-edit-description" />
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-gray-500">Promo price</label><input type="number" step="0.01" value={editModal.promoPrice ?? ''} onChange={(e) => setEditModal({ ...editModal, promoPrice: e.target.value })} className="w-full h-9 px-3 rounded-lg border text-sm" data-testid="admin-promo-edit-promoprice" /></div>
              <div><label className="text-xs text-gray-500">Normal price</label><input type="number" step="0.01" value={editModal.normalPrice ?? ''} onChange={(e) => setEditModal({ ...editModal, normalPrice: e.target.value })} className="w-full h-9 px-3 rounded-lg border text-sm" data-testid="admin-promo-edit-normalprice" /></div>
            </div>
            <label className="text-xs text-gray-500">End date</label>
            <input type="date" value={(editModal.endDate || '').slice(0, 10)} onChange={(e) => setEditModal({ ...editModal, endDate: e.target.value })} className="w-full h-9 px-3 rounded-lg border text-sm" data-testid="admin-promo-edit-enddate" />
            <button onClick={saveEdit} disabled={busy === editModal.id} className="w-full h-10 rounded-full bg-indigo-600 text-white font-semibold" data-testid="admin-promo-edit-save">Save</button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminPromoSettings = ({ settings, onSaved, onError }) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setDraft(JSON.parse(JSON.stringify(settings))); }, [settings]);
  if (!draft) return <p className="text-sm text-gray-400 py-6 text-center">Loading settings…</p>;

  const setDur = (i, key, val) => {
    const durs = [...draft.durations];
    durs[i] = { ...durs[i], [key]: key === 'enabled' ? val : Number(val) };
    setDraft({ ...draft, durations: durs });
  };
  const addDur = () => setDraft({ ...draft, durations: [...(draft.durations || []), { days: 60, priceCents: 999, enabled: true }] });
  const removeDur = (i) => setDraft({ ...draft, durations: draft.durations.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.put('/admin/price/settings', {
        currency: draft.currency,
        durations: draft.durations.map((d) => ({ days: Number(d.days), priceCents: Number(d.priceCents), enabled: !!d.enabled })),
        introFreeEnabled: !!draft.introFreeEnabled,
        introFreeDurationDays: Number(draft.introFreeDurationDays),
        maxActivePerBusiness: Number(draft.maxActivePerBusiness),
        manualFreeAllowance: Number(draft.manualFreeAllowance),
        paidPromotionsEnabled: !!draft.paidPromotionsEnabled,
        autoPublishEnabled: !!draft.autoPublishEnabled,
      });
      onSaved(res.data);
    } catch (err) { onError(err.response?.data?.detail || err.message); }
    finally { setSaving(false); }
  };

  const Toggle = ({ label, k, testid }) => (
    <label className="flex items-center justify-between py-2 border-b" data-testid={testid}>
      <span className="text-sm text-gray-700">{label}</span>
      <input type="checkbox" checked={!!draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })} className="h-5 w-5" />
    </label>
  );

  return (
    <div className="space-y-4 max-w-lg" data-testid="admin-promo-settings">
      <div>
        <p className="text-sm font-semibold text-gray-800 mb-2">Durations & prices</p>
        <div className="space-y-2">
          {draft.durations.map((d, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`admin-setting-duration-${i}`}>
              <input type="number" value={d.days} onChange={(e) => setDur(i, 'days', e.target.value)} className="w-20 h-9 px-2 rounded-lg border text-sm" data-testid={`admin-setting-days-${i}`} />
              <span className="text-xs text-gray-400">days</span>
              <span className="text-xs text-gray-400 ml-2">{draft.currency}</span>
              <input type="number" step="1" value={d.priceCents} onChange={(e) => setDur(i, 'priceCents', e.target.value)} className="w-24 h-9 px-2 rounded-lg border text-sm" title="price in cents" data-testid={`admin-setting-cents-${i}`} />
              <span className="text-xs text-gray-400">= {centsStr(d.priceCents, draft.currency)}</span>
              <label className="text-xs flex items-center gap-1 ml-auto"><input type="checkbox" checked={!!d.enabled} onChange={(e) => setDur(i, 'enabled', e.target.checked)} />on</label>
              <button onClick={() => removeDur(i)} className="p-1 text-rose-600" data-testid={`admin-setting-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={addDur} className="text-sm text-indigo-600 inline-flex items-center gap-1" data-testid="admin-setting-add-duration"><PlusCircle className="h-4 w-4" />Add duration</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-gray-500">Currency (3-letter)</label><input value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })} maxLength={3} className="w-full h-9 px-3 rounded-lg border text-sm" data-testid="admin-setting-currency" /></div>
        <div><label className="text-xs text-gray-500">Free intro duration (days)</label><input type="number" value={draft.introFreeDurationDays} onChange={(e) => setDraft({ ...draft, introFreeDurationDays: e.target.value })} className="w-full h-9 px-3 rounded-lg border text-sm" data-testid="admin-setting-introdays" /></div>
        <div><label className="text-xs text-gray-500">Max active per business</label><input type="number" value={draft.maxActivePerBusiness} onChange={(e) => setDraft({ ...draft, maxActivePerBusiness: e.target.value })} className="w-full h-9 px-3 rounded-lg border text-sm" data-testid="admin-setting-maxactive" /></div>
        <div><label className="text-xs text-gray-500">Manual free allowance</label><input type="number" value={draft.manualFreeAllowance} onChange={(e) => setDraft({ ...draft, manualFreeAllowance: e.target.value })} className="w-full h-9 px-3 rounded-lg border text-sm" data-testid="admin-setting-manualfree" /></div>
      </div>

      <div>
        <Toggle label="Free introductory promotion enabled" k="introFreeEnabled" testid="admin-setting-introenabled" />
        <Toggle label="Automatic publishing enabled" k="autoPublishEnabled" testid="admin-setting-autopublish" />
        <Toggle label="Paid promotions enabled (Stage 2 — keep OFF in preview)" k="paidPromotionsEnabled" testid="admin-setting-paidenabled" />
      </div>
      {draft.paidPromotionsEnabled && (
        <p className="text-[12px] text-amber-600" data-testid="admin-setting-paidwarning">⚠️ Paid promotions ON: paid-duration promos will require Stripe payment (Stage 2) before publishing.</p>
      )}

      <button onClick={save} disabled={saving} className="h-10 px-5 rounded-full bg-indigo-600 text-white font-semibold" data-testid="admin-setting-save">{saving ? 'Saving…' : 'Save settings'}</button>
    </div>
  );
};

export default AdminPricePromotions;
